// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useEffect } from 'react';
import { Edit3, Trash2, CheckCircle, XCircle, Plus, AlertCircle } from 'lucide-react';
import { useAuth } from '@umd-dit-ai-workspace/workspaces-component';
import { formatDate } from '../lib/utils';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Input,
  Label,
  Textarea,
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@umd-dit-ai-workspace/shared-ui';
import { useConfigMutations } from '../hooks/useConfigMutations';
import { useQuery } from '@tanstack/react-query';
import { configApi } from '../api/config';
import { getAnalysisTypes } from '../utils/prompts';
import type { ConfigurationLevel } from '../types/config';
import type { PromptWithAnalysisType } from '../types/prompt';

interface PromptEditorProps {
  level: ConfigurationLevel;
  selectedPrompt: PromptWithAnalysisType | null;
  onPromptChange: (prompt: PromptWithAnalysisType | null) => void;
  onPromptCreated?: (name: string) => void;
}

type EditorMode = 'view' | 'edit' | 'create';

export function PromptEditor({ level, selectedPrompt, onPromptChange, onPromptCreated }: PromptEditorProps) {
  // Default to create mode when no prompt is selected
  const [mode, setMode] = useState<EditorMode>(!selectedPrompt ? 'create' : 'view');
  const [editedContent, setEditedContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [newPromptName, setNewPromptName] = useState('');
  const [newAnalysisType, setNewAnalysisType] = useState('');

  const { selectedOrganizationId, selectedWorkspaceId } = useAuth();

  // Fetch prompts to get available analysis types for create mode
  const { data: prompts } = useQuery({
    queryKey: level === 'default'
      ? ['prompts', level]
      : ['prompts', level, selectedOrganizationId, selectedWorkspaceId],
    queryFn: () => configApi.getPrompts(
      level,
      level === 'workspace' ? selectedOrganizationId || undefined : undefined,
      level === 'workspace' ? selectedWorkspaceId || undefined : undefined
    ),
    enabled: level === 'default' || Boolean(selectedOrganizationId && selectedWorkspaceId),
  });

  const mutations = useConfigMutations({
    level,
    orgId: level === 'workspace' ? selectedOrganizationId || undefined : undefined,
    wsId: level === 'workspace' ? selectedWorkspaceId || undefined : undefined,
  });

  // Initialize edit content when prompt changes
  useEffect(() => {
    // When a prompt is selected, set to view mode
    if (selectedPrompt) {
      setMode('view');
      if ('content' in selectedPrompt) {
        setEditedContent(selectedPrompt.content || '');
      }
    }
    // When no prompt is selected, default to create mode
    else {
      setMode('create');
    }
  }, [selectedPrompt]);

  // Reset create form or initialize with default prompt
  useEffect(() => {
    if (mode === 'create') {
      setEditedContent('Enter your prompt content here...');
      setNewPromptName('');
      setNewAnalysisType('');
    }
  }, [mode]);

  const handleNew = () => {
    setMode('create');
    onPromptChange(null);
  };

  const handleCreate = () => {
    const promptName = newPromptName.trim();
    const analysisType = newAnalysisType.trim();

    // Require analysis type for prompts
    if (!analysisType) {
      alert('Analysis type is required for prompts');
      return;
    }

    mutations.createPrompt.mutate(
      {
        name: promptName,
        content: editedContent,
        analysisType: analysisType,
      },
      {
        onSuccess: () => {
          // After successful creation, notify parent to fetch and select the new prompt
          const fullName = `${analysisType}/${promptName}`;
          onPromptCreated?.(fullName);
        }
      }
    );
  };

  const handleSave = async () => {
    if (!selectedPrompt) return;
    setIsSaving(true);
    try {
      await mutations.updatePrompt.mutateAsync({
        name: selectedPrompt.name,
        content: editedContent,
        analysisType: selectedPrompt.analysisType,
      });
      setMode('view');
      // Re-fetch to get the updated prompt data
      const fullName = selectedPrompt.analysisType
        ? `${selectedPrompt.analysisType}/${selectedPrompt.name}`
        : selectedPrompt.name;
      onPromptCreated?.(fullName);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setMode('view');
    if (selectedPrompt && 'content' in selectedPrompt) {
      setEditedContent(selectedPrompt.content || '');
    }
  };

  const handleDeleteConfirm = () => {
    if (!selectedPrompt) return;
    mutations.deletePrompt.mutate({
      name: selectedPrompt.name,
      analysisType: selectedPrompt.analysisType,
    });
    onPromptChange(null);
  };

  // Get available analysis types
  const availableAnalysisTypes = getAnalysisTypes(prompts || []);

  // Determine current mode and content
  const error = mutations.updatePrompt.error || mutations.deletePrompt.error || mutations.createPrompt.error;

  // Determine the prompt name to display
  const promptName = mode === 'create' ? newPromptName : (selectedPrompt?.name || '');
  const displayTitle = mode === 'create' ? 'Create New Prompt' : (selectedPrompt?.name || 'New Prompt');

  // Create button group based on mode
  const actionButtons = mode !== 'view' ? (
    <>
      <Button
        onClick={mode === 'create' ? handleCreate : handleSave}
        disabled={
          mode === 'create'
            ? (!newPromptName.trim() || mutations.createPrompt.isPending)
            : (isSaving || mutations.updatePrompt.isPending)
        }
        size="sm"
      >
        <CheckCircle className="h-4 w-4" />
        {mode === 'create' ? 'Create' : 'Save'}
      </Button>
      <Button onClick={handleCancel} variant="secondary" size="sm">
        <XCircle className="h-4 w-4" />
        Cancel
      </Button>
    </>
  ) : (
    <>
      <Button onClick={handleNew} size="sm">
        <Plus className="h-4 w-4" />
        New
      </Button>
      <Button onClick={() => setMode('edit')} size="sm">
        <Edit3 className="h-4 w-4" />
        Edit
      </Button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="destructive" size="sm" disabled={mutations.deletePrompt.isPending}>
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Prompt</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{selectedPrompt?.name}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  return (
    <Card className="lg:col-span-2">
      <CardHeader>
        <div className="flex justify-between">
          <div>
            <h3 className="text-xl font-semibold">{displayTitle}</h3>
            <p className="text-sm text-muted-foreground">
              {mode === 'create' ? 'Define a new prompt' : 'Prompt Configuration'}
            </p>
            {selectedPrompt && mode === 'view' && (selectedPrompt.created_by || selectedPrompt.created_at) && (
              <div className="mt-2 space-y-1">
                {selectedPrompt.created_by && (
                  <p className="text-xs text-muted-foreground">
                    Created by: <span className="font-medium">{selectedPrompt.created_by}</span>
                  </p>
                )}
                {selectedPrompt.created_at && (
                  <p className="text-xs text-muted-foreground">
                    Created: <span className="font-medium">{formatDate(selectedPrompt.created_at)}</span>
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-2">{actionButtons}</div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {mode === 'create' && (
          <div>
            <Label htmlFor="analysis-type">Analysis Type</Label>
            <Select
              value={newAnalysisType}
              onValueChange={setNewAnalysisType}
            >
              <SelectTrigger id="analysis-type">
                <SelectValue placeholder="Select or create new type" />
              </SelectTrigger>
              <SelectContent>
                {availableAnalysisTypes.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              className="mt-2"
              placeholder="Or enter a new analysis type..."
              value={newAnalysisType}
              onChange={(e) => setNewAnalysisType(e.target.value)}
            />
          </div>
        )}

        <div>
          <Label htmlFor="prompt-name">Name</Label>
          <Input
            id="prompt-name"
            value={mode === 'create' ? newPromptName : promptName}
            onChange={(e) => mode === 'create' && setNewPromptName(e.target.value)}
            disabled={mode !== 'create'}
            placeholder="prompt-name"
            autoFocus={mode === 'create'}
          />
        </div>

        <div>
          <Label htmlFor="prompt-content">Content</Label>
          {mode !== 'view' ? (
            <Textarea
              id="prompt-content"
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="h-96 font-mono"
              placeholder="Enter your prompt content..."
            />
          ) : (
            <pre className="p-4 rounded-md overflow-auto max-h-96 bg-muted border text-sm whitespace-pre-wrap">
              {selectedPrompt?.content}
            </pre>
          )}
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{(error as Error).message}</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}